/**
 * Copy for the interactive "how the site works" guide (HowItWorksGuide),
 * approved design v3 (2026-10-19). One object per UI language; the guide
 * falls back to Arabic.
 *
 * Every claim here must match the code — keep it in step with:
 *   - commission: own products 0%, and their cash goes from the courier
 *     straight to the store owner — never through the wallet
 *     (server/lib/ownProductDelivery.ts); wholesale resale = the live
 *     platform_commission_rate of the MARGIN only ({rate} placeholder, read
 *     from GET /api/settings/dropshipping), paid out through the wallets —
 *     server/lib/walletMath.ts;
 *   - wholesale visibility: canSeeWholesaleByRole (server/routes/listings.ts);
 *   - resale minimum price: calcMinResalePrice (shared/constants.ts);
 *   - wholesaler accounts cannot sell to consumers or become a store
 *     (server/lib/accountPolicy.ts, /api/auth/activate-virtual-store);
 *   - store orders left pending 24h are auto-cancelled (server/cron/storeHealth.ts).
 * No guaranteed profits, no "sell without any effort" promises.
 *
 * Placeholders: {rate} commission %, {fee}/{net} worked-example amounts,
 * {n}/{total} step counter.
 */

export type GuideRole = "buy" | "sell" | "store" | "trade";

export interface GuideStep {
  title: string;
  text: string;
  note: string;
}

export interface GuideHighlight {
  title: string;
  text: string;
}

export interface GuideRoleCopy {
  tab: string;
  tabHint: string;
  title: string;
  intro: string;
  badges: [string, string, string];
  highlights?: GuideHighlight[];
  /** Trade only: the three boxes of the "how your goods reach buyers" flow. */
  flow?: [string, string, string];
  flowTitle?: string;
  steps: GuideStep[];
  calloutTitle: string;
  calloutText: string;
}

export interface GuideCopy {
  eyebrow: string;
  title: string;
  subtitle: string;
  close: string;
  rolesLabel: string;
  stepsLabel: string;
  stepCounter: string;
  prev: string;
  next: string;
  done: string;
  currency: string;
  calc: {
    title: string;
    sub: string;
    cost: string;
    price: string;
    margin: string;
    fee: string;
    net: string;
    disclaimer: string;
    belowCost: string;
    belowMin: string;
  };
  friday: { summary: string; text: string; cta: string };
  cta: {
    buy: string;
    sell: string;
    storeOpen: string;
    storeManage: string;
    storeSeparate: string;
    storeMore: string;
    tradeRegister: string;
    tradeDashboard: string;
    sellStoreOwn: string;
    sellWholesale: string;
    separateCustomer: string;
  };
  roles: Record<GuideRole, GuideRoleCopy>;
}

const ar: GuideCopy = {
  eyebrow: "✦ دليلك إلى سوق العراق",
  title: "كيف تريد الاستفادة من سوق العراق؟",
  subtitle: "سواء كنت مشتريًا أو بائعًا أو صاحب متجر أو تاجر جملة، اكتشف الطريقة المناسبة لك.",
  close: "إغلاق",
  rolesLabel: "اختر طريقة استخدامك لسوق العراق",
  stepsLabel: "خطوات الدليل",
  stepCounter: "الخطوة {n} من {total}",
  prev: "السابق",
  next: "التالي",
  done: "انتهيت ✓",
  currency: "د.ع",
  calc: {
    title: "جرّب حساب ربحك من منتج تاجر جملة",
    sub: "أدخل سعر الجملة وسعر بيعك للزبون، لتعرف الهامش وحصة الموقع وصافي ربحك. مثال توضيحي، وليس توقعًا للمبيعات.",
    cost: "سعر الجملة (دينار عراقي)",
    price: "سعر البيع (دينار عراقي)",
    margin: "هامش البيع",
    fee: "حصة الموقع ({rate}٪)",
    net: "صافي ربحك",
    disclaimer: "تُحتسب حصة الموقع ({rate}٪) على هامش إعادة البيع فقط، بعد إتمام الطلب وتسليمه، وفق النسبة المعتمدة في إعدادات الموقع، ويُضاف صافي ربحك إلى محفظتك. أما منتجات متجرك الخاصة فلا يقتطع الموقع منها أي عمولة، ويسلّمك المندوب ثمنها مباشرة.",
    belowCost: "ليكون لك ربح، يجب أن يكون سعر البيع أعلى من سعر الجملة، ولا يقل عن الحد الأدنى الذي يحدده النظام.",
    belowMin: "هذا السعر أقل من الحد الأدنى الذي يقبله النظام لهذا المنتج: {min}. اختر سعرًا لا يقل عنه.",
  },
  friday: {
    summary: "وماذا عن «سوق الجمعة»؟",
    text: "يمكنك نشر إعلان خاص بسوق الجمعة، فيظهر في سوقه يوم الجمعة فقط بتوقيت العراق. وإذا نشرته في يوم آخر يُجدول تلقائيًا ليظهر يوم الجمعة التالي. وهو مسار مختلف عن عروض الجملة والمتاجر.",
    cta: "انشر في سوق الجمعة",
  },
  cta: {
    buy: "ابدأ البحث",
    sell: "أضف إعلانك",
    storeOpen: "افتح متجرك مجانًا",
    storeManage: "اذهب إلى منتجاتي",
    storeSeparate: "افتح متجرًا بحساب منفصل",
    storeMore: "تعرّف أكثر على المتاجر الافتراضية",
    tradeRegister: "سجّل كتاجر جملة",
    tradeDashboard: "اذهب إلى لوحة الجملة",
    sellStoreOwn: "أضف منتجًا خاصًا لمتجرك",
    sellWholesale: "أضف عرض جملة",
    separateCustomer: "لإعلان شخصي افتح حساب زبون منفصلًا",
  },
  roles: {
    buy: {
      tab: "أريد الشراء",
      tabHint: "استعرض المنتجات وأكمل طلبك",
      title: "اكتشف المنتجات بحسب القسم والمحافظة",
      intro: "ابحث عن الإعلان أو المنتج المناسب لك، واقرأ تفاصيله، ثم أكمل العملية بالطريقة التي يتيحها نوع العرض.",
      badges: ["أقسام واضحة", "بحث حسب المحافظة", "تواصل أو طلب من المتجر"],
      steps: [
        { title: "ابحث", text: "تصفّح الأقسام أو اكتب اسم المنتج، وحدّد المحافظة لتحصل على نتائج أقرب إليك.", note: "يمكنك تصفّح العروض دون تسجيل." },
        { title: "اقرأ التفاصيل", text: "افتح المنتج وراجع الصور والوصف والسعر والموقع وطريقة الشراء المتاحة.", note: "عروض الجملة لا تُعرض للزبائن، وتصلك منتجاتها عبر المتاجر بسعر التجزئة." },
        { title: "أكمل الخطوة التالية", text: "في أقسام التسوق والرياضة والأطفال والمستلزمات الطبية وقطع الغيار تشتري عبر السلة، سواء كان البائع متجرًا أو شخصًا، وتدفع عند الاستلام. وفي الأقسام الأخرى كالسيارات والعقارات والخدمات تتواصل مع صاحب الإعلان مباشرة.", note: "افحص المنتج عند الاستلام، ولا تعطِ رمز الاستلام إلا بعد رضاك عنه." },
      ],
      calloutTitle: "اعرف طريقة الشراء قبل الطلب",
      calloutText: "طريقة الشراء يحددها القسم: أقسام البضائع تعمل بالسلة وإدخال بيانات التسليم والدفع عند الاستلام، وبقية الأقسام تعتمد على التواصل المباشر والاتفاق بين المشتري والبائع.",
    },
    sell: {
      tab: "أريد بيع غرض",
      tabHint: "انشر إعلانك للمهتمين",
      title: "حوّل أغراضك إلى فرص بيع",
      intro: "انشر إعلانًا لغرض جديد أو مستعمل، وتواصل مع المشترين المهتمين داخل سوق العراق.",
      badges: ["صور واضحة", "سعر وموقع محددان", "تواصل مع المهتمين"],
      steps: [
        { title: "سجّل الدخول", text: "أنشئ حسابًا أو ادخل إلى حسابك، ثم اختر «أضف إعلانك».", note: "لا تحتاج إلى التواصل مع الإدارة لنشر إعلان شخصي عادي." },
        { title: "جهّز الإعلان", text: "أضف الصور والعنوان والوصف والسعر والمحافظة والقسم الصحيح.", note: "الصور الحقيقية والمعلومات الدقيقة تجعل إعلانك أكثر جدية." },
        { title: "تابع المهتمين", text: "انشر الإعلان وتابع المشترين: في أقسام التسوق والرياضة والأطفال والمستلزمات الطبية وقطع الغيار تصلك الطلبات عبر السلة وتتابعها من «طلبات إعلاناتي الخاصة»، وفي بقية الأقسام يتواصل معك المشترون مباشرة.", note: "في الأقسام ذات التواصل المباشر يتم البيع بالاتفاق بينك وبين المشتري." },
      ],
      calloutTitle: "نشر ذاتي وبسيط",
      calloutText: "لكل حساب عدد من الإعلانات المجانية، ولإضافة المزيد تواصل مع الإدارة. حسابات المتاجر وتجار الجملة تبيع من خلال متاجرها وعروضها، لا بإعلانات شخصية.",
    },
    store: {
      tab: "أفتح متجرًا افتراضيًا",
      tabHint: "اعرض بضاعة التجار دون شرائها",
      title: "ابدأ مشروع بيع من بيتك، دون شراء مخزون",
      intro: "افتح متجرًا افتراضيًا مجانًا باسمك، واعرض فيه منتجاتك الخاصة إن وُجدت، وبضائع تجار الجملة المؤهلة لإعادة البيع دون أن تشتريها مسبقًا. أنت تختار المنتجات وتحدد سعر البيع ضمن الضوابط، ثم تسوّق لمتجرك وتتابع طلباته. ثمن منتجاتك الخاصة يقبضه مندوب التوصيل من الزبون ويسلّمه لك مباشرة، وربحك من بضائع الجملة يُضاف إلى محفظتك بعد إتمام البيع.",
      badges: ["فتح المتجر مجاني", "منتجاتك الخاصة بلا عمولة", "بضائع الجملة دون شراء مسبق"],
      highlights: [
        { title: "بيع منتجاتك الخاصة دون عمولة للموقع", text: "عندما تبيع منتجًا تملكه أنت، يقبض مندوب التوصيل ثمنه من الزبون ويسلّمه لك مباشرة. لا يقتطع الموقع منه أي نسبة، ولا يمر المبلغ عبر محفظتك في الموقع." },
        { title: "أعد بيع بضائع الجملة دون مخزون", text: "اعرض منتجات التجار المؤهلة دون شرائها مسبقًا، وحصة الموقع {rate}٪ من هامش ربحك فقط، لا من سعر البيع، ويصل ربحك عبر محفظتك في الموقع." },
      ],
      steps: [
        { title: "افتح متجرك مجانًا", text: "أنشئ حسابًا عاديًا، ثم فعّل متجرك الافتراضي من صفحة «انضم للعمل معنا» واختر له اسمًا يظهر للمتسوقين. لا رسوم لفتح المتجر ولا لإضافة المنتجات، ولا تحتاج إلى محل على الأرض.", note: "تفعيل المتجر يحوّل حسابك إلى حساب متجر، فلا ينشر إعلانات شخصية ولا يشتري من المتاجر أو الأفراد. إن أردت ذلك فاستخدم حساب زبون منفصلًا." },
        { title: "أضف منتجاتك الخاصة", text: "إن كانت لديك بضاعة تملكها، أضفها من صفحة «منتجاتي» وحدد سعرها. تجهّزها أنت وتسلّمها لمندوب التوصيل عند الطلب.", note: "لا عمولة للموقع على منتجاتك الخاصة: يقبض المندوب ثمنها من الزبون عند التسليم ويسلّمه لك مباشرة، ولا يُضاف إلى محفظتك." },
        { title: "تصفّح بضائع الجملة", text: "ادخل إلى أسواق الجملة وتصفّح منتجات التجار. أسعار الجملة تظهر لأصحاب المتاجر والتجار فقط، ولا تُعرض للزبائن.", note: "تستطيع إعادة بيع المنتجات التي يتيحها التاجر للمتاجر الافتراضية فقط." },
        { title: "أضفها إلى مخزونك السحابي", text: "اضغط «إضافة لمحلي» على المنتج المؤهل، فيُضاف إلى مخزونك السحابي دون شراء القطعة أو دفع أي مبلغ. التاجر يخزّنها ويجهّزها ويشحنها.", note: "لكل متجر حد لعدد منتجات الجملة التي يمكن إضافتها." },
        { title: "حدّد سعر البيع", text: "اختر سعر البيع للزبون. يضع النظام حدًا أدنى أعلى من سعر الجملة، ويعرض لك الهامش وحصة الموقع وصافي ربحك قبل الحفظ.", note: "مثال: جملة ٢٠ ألفًا وبيع ٣٠ ألفًا ← الهامش ١٠ آلاف، حصة الموقع {rate}٪ منه ({fee})، ويبقى لك {net}." },
        { title: "روّج وتابع الطلبات", text: "شارك رابط متجرك وروّج لمنتجاته. عندما يطلب زبون منتج جملة، اقبل الطلب ليجهّزه التاجر ويشحنه.", note: "الطلب الذي لا تقبله خلال ٢٤ ساعة يُلغى تلقائيًا ويُسجَّل مخالفة على متجرك، وبعد ٣ مخالفات يُجمَّد المتجر ولا يمكن الدخول إليه حتى تعيد الإدارة تفعيله. ولا توجد أرباح مضمونة؛ المبيعات تعتمد على اختيارك وتسويقك ومتابعتك." },
        { title: "احصل على عائدك", text: "في بضائع الجملة: بعد تسليم الطلب وتأكيده برمز الاستلام، يُحتسب ربحك ويُضاف إلى محفظتك عبر النظام المالي للموقع، ويمكنك طلب سحبه. أما منتجاتك الخاصة فيسلّمك المندوب ثمنها مباشرة عند التسليم.", note: "حصة الموقع تُحتسب على الفرق بين سعر الجملة وسعر البيع فقط، لا على كامل السعر." },
      ],
      calloutTitle: "الشفافية جزء من الفرصة",
      calloutText: "لا يتطلب فتح المتجر أو إضافة المنتجات أي دفعة. في إعادة بيع بضائع الجملة تُحتسب حصة الموقع من هامش الربح عند إتمام البيع، وتُوزَّع المستحقات عبر محافظ الموقع. وفي منتجاتك الخاصة لا عمولة للموقع، ويسلّمك المندوب ثمنها مباشرة. نجاح المتجر يحتاج اختيار منتجات مناسبة وتسويقًا ومتابعة للطلبات، والأرباح مرتبطة بالمبيعات الفعلية.",
    },
    trade: {
      tab: "أنا تاجر جملة",
      tabHint: "وسّع منافذ بيع بضاعتك",
      title: "حوّل المتاجر المشاركة إلى منافذ بيع إضافية لبضاعتك",
      intro: "لديك محل أو مخزن جملة؟ اعرض بضاعتك في سوق العراق لأصحاب المتاجر الحقيقية والافتراضية. كل متجر يختار منتجاتك ويعرضها لزبائنه بسعر التجزئة، فتتسع منافذ بيعك إلى جانب محلك وقنواتك الحالية، وأنت تحتفظ بسعر الجملة الذي تحدده.",
      badges: ["منافذ بيع إضافية", "سعر الجملة تحدده أنت", "محلك وقنواتك تبقى كما هي"],
      flowTitle: "كيف تصل بضاعتك إلى الزبائن؟",
      flow: ["أنت: تاجر الجملة", "المتاجر الحقيقية والافتراضية", "الزبائن بسعر التجزئة"],
      highlights: [
        { title: "منافذ بيع تتوسع معك", text: "كل متجر نشط يضيف منتجاتك قد يصبح منفذ بيع جديدًا لبضاعتك، دون أن تفتح فروعًا أو تستأجر محلات." },
        { title: "سعر الجملة محمي", text: "عروض الجملة وأسعارها لا تُعرض للزبائن، فلا تنافس قناة الجملة أسعار التجزئة أمام المستهلك." },
      ],
      steps: [
        { title: "سجّل كتاجر جملة", text: "اختر «تسجيل كتاجر جملة» وأدخل بيانات نشاطك برقم هاتف وبريد لم يُستخدما في حساب آخر. تراجع الإدارة الحساب قبل تفعيله.", note: "حساب تاجر الجملة مخصص لعرض البضاعة للمتاجر، لا للبيع المباشر للمستهلك بسعر الجملة." },
        { title: "أضف بضاعتك وأسعار الجملة", text: "أضف صورًا حقيقية ووصفًا واضحًا وسعر الجملة والكمية المتاحة والحد الأدنى للطلب.", note: "تُعرض بضائع الجملة في أقسام: التسوق، الرياضة، الأطفال، المستلزمات الطبية، قطع الغيار." },
        { title: "اسمح للمتاجر بإعادة البيع", text: "فعّل خيار «متاح للمتاجر الافتراضية» على المنتجات التي تريد أن تعيد المتاجر بيعها دون شرائها مسبقًا.", note: "تختار ذلك لكل منتج على حدة." },
        { title: "تصل عروضك إلى المتاجر", text: "تظهر عروض الجملة وأسعارها لأصحاب المتاجر والتجار المسجلين فقط، ولا تُعرض للزبائن كأسعار جملة.", note: "هذا الفصل يحمي سعر الجملة من منافسة أسعار التجزئة أمام الزبون." },
        { title: "المتاجر تعرض بضاعتك بالتجزئة", text: "يضيف أصحاب المتاجر منتجاتك المتاحة إلى متاجرهم بسعر تجزئة أعلى من سعر الجملة، والنظام يفرض حدًا أدنى لهذا السعر. ويمكن للمحلات أيضًا شراء بضاعتك بالجملة مباشرة وفق الحد الأدنى للطلب.", note: "كل متجر نشط قد يصبح منفذ بيع جديدًا، لكن عدد المتاجر والمبيعات غير مضمون." },
        { title: "جهّز الطلبات واستلم مستحقاتك", text: "عندما يطلب زبون منتجك من أحد المتاجر ويقبل المتجر الطلب، تجهّزه وتشحنه، وبعد تأكيد التسليم يُضاف سعر الجملة المستحق لك إلى محفظتك.", note: "لا يُطلب منك ترك محلك أو قنوات بيعك الحالية." },
        { title: "تريد البيع للزبائن مباشرة؟", text: "حساب تاجر الجملة لا يبيع للمستهلك ولا يتحول إلى متجر. إذا أردت البيع بالتجزئة، أنشئ حساب زبون منفصلًا برقم هاتف وبريد مختلفين، وفعّل منه متجرًا افتراضيًا، واعرض فيه منتجاتك بسعر التجزئة.", note: "سياسة الموقع أن يكون سعر التجزئة أعلى من سعر الجملة، ولا تُعرض أسعار الجملة للزبائن." },
      ],
      calloutTitle: "الجملة للمحلات، والتجزئة عبر المتاجر",
      calloutText: "عروض الجملة وأسعارها مخصصة لحسابات التجار وأصحاب المتاجر، ولا تُعرض للزبائن كعروض جملة. أما الزبائن فيرون منتجاتك عندما تعرضها المتاجر بسعر التجزئة.",
    },
  },
};

const en: GuideCopy = {
  eyebrow: "✦ Your guide to Iraq Sooq",
  title: "How would you like to use Iraq Sooq?",
  subtitle: "Whether you are a buyer, a seller, a store owner or a wholesaler, find the path that fits you.",
  close: "Close",
  rolesLabel: "Choose how you use Iraq Sooq",
  stepsLabel: "Guide steps",
  stepCounter: "Step {n} of {total}",
  prev: "Previous",
  next: "Next",
  done: "Done ✓",
  currency: "IQD",
  calc: {
    title: "Estimate your profit on a wholesaler's product",
    sub: "Enter the wholesale price and your selling price to see the margin, the site's share and your net profit. An illustration, not a sales forecast.",
    cost: "Wholesale price (IQD)",
    price: "Selling price (IQD)",
    margin: "Margin",
    fee: "Site share ({rate}%)",
    net: "Your net profit",
    disclaimer: "The site's share ({rate}%) is taken from the resale margin only, after the order is delivered, at the rate set in the site settings; your net profit is added to your wallet. Your store's own products carry no site commission, and the courier hands you their price directly.",
    belowCost: "To make a profit, the selling price must be above the wholesale price and not below the minimum the system sets.",
    belowMin: "This price is below the minimum the system accepts for this product: {min}. Choose a price at or above it.",
  },
  friday: {
    summary: "What about the Friday market?",
    text: "You can post a Friday-market ad; it appears in that market on Fridays only (Iraq time). Posted on another day, it is scheduled automatically for the next Friday. It is separate from wholesale offers and stores.",
    cta: "Post in the Friday market",
  },
  cta: {
    buy: "Start searching",
    sell: "Post your ad",
    storeOpen: "Open your store for free",
    storeManage: "Go to my products",
    storeSeparate: "Open a store with a separate account",
    storeMore: "Learn more about virtual stores",
    tradeRegister: "Register as a wholesaler",
    tradeDashboard: "Go to the wholesale dashboard",
    sellStoreOwn: "Add your own product to your store",
    sellWholesale: "Add a wholesale offer",
    separateCustomer: "For a personal ad, open a separate customer account",
  },
  roles: {
    buy: {
      tab: "I want to buy",
      tabHint: "Browse products and order",
      title: "Find products by category and province",
      intro: "Search for the ad or product you need, read its details, then complete the purchase the way that offer allows.",
      badges: ["Clear categories", "Search by province", "Contact or order from a store"],
      steps: [
        { title: "Search", text: "Browse the categories or type a product name, and pick a province for results closer to you.", note: "You can browse without signing in." },
        { title: "Read the details", text: "Open the product and check the photos, description, price, location and how it can be bought.", note: "Wholesale offers are not shown to customers; their products reach you through stores at retail prices." },
        { title: "Take the next step", text: "In Shopping, Sports, Kids, Medical supplies and Spare parts you buy through the cart — from a store or a person — and pay cash on delivery. In other categories such as cars, real estate and services you contact the advertiser directly.", note: "Check the product on delivery and only give the delivery code once you are satisfied." },
      ],
      calloutTitle: "Know how to buy before you order",
      calloutText: "The category decides: goods categories use the cart, delivery details and cash on delivery; the other categories work through direct contact and agreement between buyer and seller.",
    },
    sell: {
      tab: "I want to sell an item",
      tabHint: "Post your ad",
      title: "Turn your items into sales",
      intro: "Post an ad for a new or used item and talk to interested buyers inside Iraq Sooq.",
      badges: ["Clear photos", "Price and location", "Talk to buyers"],
      steps: [
        { title: "Sign in", text: "Create an account or sign in, then choose \"Post your ad\".", note: "You don't need to contact the admins to post a normal personal ad." },
        { title: "Prepare the ad", text: "Add photos, a title, a description, the price, the province and the right category.", note: "Real photos and accurate details make your ad more credible." },
        { title: "Follow up with buyers", text: "Publish the ad and follow up: in Shopping, Sports, Kids, Medical supplies and Spare parts orders arrive through the cart and you follow them from your account menu; in other categories buyers contact you directly.", note: "In direct-contact categories the sale is agreed between you and the buyer." },
      ],
      calloutTitle: "Simple self-service posting",
      calloutText: "Each account has a number of free ads; to add more, contact the admins. Store and wholesaler accounts sell through their stores and offers, not personal ads.",
    },
    store: {
      tab: "I'll open a virtual store",
      tabHint: "Sell wholesalers' goods without buying them",
      title: "Start a selling business from home, without buying stock",
      intro: "Open a free virtual store under your name and sell your own products, if you have any, plus eligible wholesalers' goods without buying them first. You choose the products and set the selling price within the rules, then promote your store and follow its orders. The courier collects the price of your own products from the customer and hands it straight to you; your profit on wholesale goods is added to your wallet once the sale completes.",
      badges: ["Free to open", "Own products: no commission", "Wholesale goods: no upfront purchase"],
      highlights: [
        { title: "Sell your own products with no site commission", text: "When you sell a product you own, the courier collects its price from the customer and hands it straight to you. The site takes no share of it, and the money does not pass through your site wallet." },
        { title: "Resell wholesale goods without stock", text: "List eligible wholesalers' products without buying them first; the site's share is {rate}% of your margin only, not of the selling price, and your profit reaches you through your site wallet." },
      ],
      steps: [
        { title: "Open your store for free", text: "Create a normal account, then activate your virtual store from the \"Work with us\" page and give it a name shoppers will see. Opening a store and adding products are free, and no physical shop is needed.", note: "Activating a store turns your account into a store account: it can no longer post personal ads or buy from stores or individuals. Use a separate customer account for that." },
        { title: "Add your own products", text: "If you have goods you own, add them from \"My products\" and set their price. You prepare them and hand them to the courier when ordered.", note: "No site commission on your own products: the courier collects their price on delivery and hands it straight to you; it is not added to your wallet." },
        { title: "Browse wholesale goods", text: "Open the wholesale markets and browse wholesalers' products. Wholesale prices are shown only to store owners and traders, never to customers.", note: "You can resell only the products a wholesaler makes available to virtual stores." },
        { title: "Add them to your cloud inventory", text: "Tap \"Add to my store\" on an eligible product; it joins your cloud inventory without buying the item or paying anything. The wholesaler stores, prepares and ships it.", note: "Each store has a limit on how many wholesale products it can add." },
        { title: "Set your selling price", text: "Choose the price for customers. The system sets a minimum above the wholesale price and shows your margin, the site's share and your net profit before you save.", note: "Example: wholesale 20,000, sale 30,000 → margin 10,000; the site keeps {rate}% of it ({fee}) and you keep {net}." },
        { title: "Promote and follow orders", text: "Share your store link and promote its products. When a customer orders a wholesale product, accept the order so the wholesaler prepares and ships it.", note: "An order you don't accept within 24 hours is cancelled automatically and counts as a strike; after 3 strikes the store is frozen and you can't sign in until the admins reactivate it. Profits aren't guaranteed; sales depend on your choices, promotion and follow-up." },
        { title: "Collect your earnings", text: "Wholesale goods: once the order is delivered and confirmed with the delivery code, your profit is calculated and added to your wallet through the site's payment system, and you can request a payout. For your own products, the courier hands you their price directly on delivery.", note: "The site's share is taken from the difference between the wholesale and selling prices only, never from the whole price." },
      ],
      calloutTitle: "Transparency is part of the opportunity",
      calloutText: "Opening a store or adding products requires no payment. On resold wholesale goods the site's share comes from the margin when the sale completes, and payouts go through the site wallets. On your own products there is no site commission, and the courier hands you their price directly. A successful store needs good product choices, promotion and order follow-up, and profits depend on actual sales.",
    },
    trade: {
      tab: "I'm a wholesaler",
      tabHint: "Widen your sales outlets",
      title: "Turn participating stores into extra outlets for your goods",
      intro: "Own a wholesale shop or warehouse? Show your goods on Iraq Sooq to real and virtual store owners. Each store picks your products and sells them to its customers at retail, widening your outlets alongside your shop and existing channels, while you keep the wholesale price you set.",
      badges: ["Extra sales outlets", "You set the wholesale price", "Keep your shop and channels"],
      flowTitle: "How do your goods reach customers?",
      flow: ["You: the wholesaler", "Real and virtual stores", "Customers at retail prices"],
      highlights: [
        { title: "Outlets that grow with you", text: "Every active store that adds your products can become a new outlet for your goods — without opening branches or renting shops." },
        { title: "Your wholesale price stays protected", text: "Wholesale offers and prices are not shown to customers, so your wholesale channel never undercuts retail prices in front of consumers." },
      ],
      steps: [
        { title: "Register as a wholesaler", text: "Choose \"Register as Wholesaler\" and enter your business details, with a phone number and email not used by another account. The admins review the account before activating it.", note: "A wholesaler account offers goods to stores; it does not sell to consumers at wholesale prices." },
        { title: "Add your goods and wholesale prices", text: "Add real photos, a clear description, the wholesale price, the available quantity and the minimum order.", note: "Wholesale goods are listed in: Shopping, Sports, Kids, Medical supplies and Spare parts." },
        { title: "Allow stores to resell", text: "Turn on \"Available to virtual stores\" for the products you want stores to resell without buying them first.", note: "You choose this product by product." },
        { title: "Your offers reach stores", text: "Wholesale offers and prices are shown only to registered store owners and traders, not to customers as wholesale prices.", note: "This separation keeps wholesale prices from competing with retail in front of the customer." },
        { title: "Stores sell your goods at retail", text: "Store owners add your available products to their stores at a retail price above wholesale, and the system enforces a minimum. Shops can also buy your goods wholesale directly, subject to the minimum order.", note: "Every active store may become a new outlet, but the number of stores and sales isn't guaranteed." },
        { title: "Fulfil orders and get paid", text: "When a customer orders your product from a store and the store accepts it, you prepare and ship it; after delivery is confirmed, the wholesale amount due to you is added to your wallet.", note: "You don't have to give up your shop or your current sales channels." },
        { title: "Want to sell to consumers directly?", text: "A wholesaler account cannot sell to consumers or become a store. To sell at retail, create a separate customer account with a different phone number and email, activate a virtual store from it, and list your products at retail prices.", note: "The site's policy is that retail prices are higher than wholesale prices, and wholesale prices are never shown to customers." },
      ],
      calloutTitle: "Wholesale for shops, retail through stores",
      calloutText: "Wholesale offers and prices are reserved for traders and store owners and are not shown to customers as wholesale offers. Customers see your products when stores list them at retail prices.",
    },
  },
};

const tr: GuideCopy = {
  eyebrow: "✦ Irak Pazarı rehberiniz",
  title: "Irak Pazarı'ndan nasıl yararlanmak istersiniz?",
  subtitle: "Alıcı, satıcı, mağaza sahibi ya da toptancı olun; size uygun yolu keşfedin.",
  close: "Kapat",
  rolesLabel: "Irak Pazarı'nı nasıl kullanacağınızı seçin",
  stepsLabel: "Rehber adımları",
  stepCounter: "Adım {n} / {total}",
  prev: "Önceki",
  next: "Sonraki",
  done: "Bitti ✓",
  currency: "IQD",
  calc: {
    title: "Bir toptancı ürününden kârınızı hesaplayın",
    sub: "Toptan fiyatı ve müşteriye satış fiyatınızı girin; marjı, sitenin payını ve net kârınızı görün. Bir örnektir, satış tahmini değildir.",
    cost: "Toptan fiyat (IQD)",
    price: "Satış fiyatı (IQD)",
    margin: "Marj",
    fee: "Site payı (%{rate})",
    net: "Net kârınız",
    disclaimer: "Sitenin payı (%{rate}) yalnızca yeniden satış marjından, sipariş teslim edildikten sonra ve site ayarlarındaki orana göre alınır; net kârınız cüzdanınıza eklenir. Mağazanızın kendi ürünlerinden site komisyon almaz; kurye bedelini doğrudan size verir.",
    belowCost: "Kâr için satış fiyatı toptan fiyatın üzerinde olmalı ve sistemin belirlediği alt sınırın altında olmamalıdır.",
    belowMin: "Bu fiyat, sistemin bu ürün için kabul ettiği alt sınırın altında: {min}. Bu tutar veya üzerinde bir fiyat seçin.",
  },
  friday: {
    summary: "Cuma pazarı ne olacak?",
    text: "Cuma pazarına özel ilan verebilirsiniz; ilan bu pazarda yalnızca cuma günleri (Irak saati) görünür. Başka bir gün verilirse bir sonraki cumaya otomatik planlanır. Toptan teklifler ve mağazalardan ayrı bir yoldur.",
    cta: "Cuma pazarında ilan ver",
  },
  cta: {
    buy: "Aramaya başla",
    sell: "İlan ver",
    storeOpen: "Mağazanı ücretsiz aç",
    storeManage: "Ürünlerime git",
    storeSeparate: "Ayrı bir hesapla mağaza aç",
    storeMore: "Sanal mağazalar hakkında daha fazla bilgi",
    tradeRegister: "Toptancı olarak kaydol",
    tradeDashboard: "Toptan paneline git",
    sellStoreOwn: "Mağazana kendi ürününü ekle",
    sellWholesale: "Toptan teklif ekle",
    separateCustomer: "Kişisel ilan için ayrı bir müşteri hesabı aç",
  },
  roles: {
    buy: {
      tab: "Satın almak istiyorum",
      tabHint: "Ürünlere göz atın ve sipariş verin",
      title: "Ürünleri kategoriye ve ile göre bulun",
      intro: "İhtiyacınız olan ilanı veya ürünü arayın, ayrıntılarını okuyun ve teklifin izin verdiği şekilde işlemi tamamlayın.",
      badges: ["Net kategoriler", "İle göre arama", "İletişim veya mağazadan sipariş"],
      steps: [
        { title: "Arayın", text: "Kategorilere göz atın veya ürün adını yazın; size yakın sonuçlar için bir il seçin.", note: "Kayıt olmadan göz atabilirsiniz." },
        { title: "Ayrıntıları okuyun", text: "Ürünü açın; fotoğrafları, açıklamayı, fiyatı, konumu ve satın alma yöntemini inceleyin.", note: "Toptan teklifler müşterilere gösterilmez; bu ürünler size mağazalar aracılığıyla perakende fiyatla ulaşır." },
        { title: "Sonraki adımı atın", text: "Alışveriş, Spor, Çocuk, Tıbbi malzemeler ve Yedek parça kategorilerinde — satıcı mağaza da olsa kişi de olsa — sepetle alır ve kapıda ödersiniz. Araba, emlak ve hizmetler gibi diğer kategorilerde ilan sahibiyle doğrudan iletişime geçersiniz.", note: "Ürünü teslimatta kontrol edin ve teslim kodunu ancak memnun kaldığınızda verin." },
      ],
      calloutTitle: "Sipariş vermeden önce satın alma yolunu bilin",
      calloutText: "Bunu kategori belirler: mal kategorileri sepet, teslimat bilgileri ve kapıda ödemeyle çalışır; diğer kategoriler alıcı ile satıcı arasında doğrudan iletişim ve anlaşmayla yürür.",
    },
    sell: {
      tab: "Bir eşya satmak istiyorum",
      tabHint: "İlanınızı yayınlayın",
      title: "Eşyalarınızı satışa dönüştürün",
      intro: "Yeni veya ikinci el bir eşya için ilan verin ve Irak Pazarı içinde ilgilenen alıcılarla konuşun.",
      badges: ["Net fotoğraflar", "Fiyat ve konum", "Alıcılarla iletişim"],
      steps: [
        { title: "Giriş yapın", text: "Hesap oluşturun veya giriş yapın, ardından \"İlan ver\"i seçin.", note: "Normal bir kişisel ilan için yönetimle iletişime geçmeniz gerekmez." },
        { title: "İlanı hazırlayın", text: "Fotoğrafları, başlığı, açıklamayı, fiyatı, ili ve doğru kategoriyi ekleyin.", note: "Gerçek fotoğraflar ve doğru bilgiler ilanınızı daha güvenilir kılar." },
        { title: "Alıcıları takip edin", text: "İlanı yayınlayın ve takip edin: Alışveriş, Spor, Çocuk, Tıbbi malzemeler ve Yedek parça kategorilerinde siparişler sepetle gelir ve bunları hesap menünüzden izlersiniz; diğer kategorilerde alıcılar size doğrudan ulaşır.", note: "Doğrudan iletişimli kategorilerde satış, sizinle alıcı arasındaki anlaşmayla yapılır." },
      ],
      calloutTitle: "Basit, kendi kendine yayın",
      calloutText: "Her hesabın belirli sayıda ücretsiz ilanı vardır; daha fazlası için yönetimle iletişime geçin. Mağaza ve toptancı hesapları kişisel ilanlarla değil, mağazaları ve teklifleriyle satış yapar.",
    },
    store: {
      tab: "Sanal mağaza açacağım",
      tabHint: "Toptancı mallarını satın almadan satın",
      title: "Evden, stok satın almadan bir satış işi kurun",
      intro: "Adınıza ücretsiz bir sanal mağaza açın; varsa kendi ürünlerinizi ve uygun toptancı mallarını önceden satın almadan satın. Ürünleri siz seçer, satış fiyatını kurallar içinde belirlersiniz; ardından mağazanızı tanıtır ve siparişleri takip edersiniz. Kendi ürünlerinizin bedelini kurye müşteriden tahsil edip doğrudan size verir; toptan mallardan kârınız satış tamamlanınca cüzdanınıza eklenir.",
      badges: ["Açmak ücretsiz", "Kendi ürünleriniz: komisyonsuz", "Toptan mallar: ön alım yok"],
      highlights: [
        { title: "Kendi ürünlerinizi site komisyonu olmadan satın", text: "Size ait bir ürünü sattığınızda kurye bedelini müşteriden tahsil edip doğrudan size verir. Site bundan hiçbir pay almaz ve tutar sitedeki cüzdanınızdan geçmez." },
        { title: "Toptan malları stoksuz yeniden satın", text: "Uygun toptancı ürünlerini önceden satın almadan listeleyin; sitenin payı satış fiyatının değil, yalnızca marjınızın %{rate}'sidir ve kârınız size sitedeki cüzdanınız üzerinden ulaşır." },
      ],
      steps: [
        { title: "Mağazanızı ücretsiz açın", text: "Normal bir hesap oluşturun, ardından \"Bizimle çalışın\" sayfasından sanal mağazanızı etkinleştirip alışverişçilerin göreceği bir ad verin. Mağaza açmak ve ürün eklemek ücretsizdir; fiziksel bir dükkân gerekmez.", note: "Mağazayı etkinleştirmek hesabınızı mağaza hesabına çevirir: artık kişisel ilan veremez, mağazalardan veya kişilerden satın alamaz. Bunun için ayrı bir müşteri hesabı kullanın." },
        { title: "Kendi ürünlerinizi ekleyin", text: "Size ait mallarınız varsa \"Ürünlerim\" sayfasından ekleyip fiyatını belirleyin. Sipariş gelince siz hazırlar ve kuryeye teslim edersiniz.", note: "Kendi ürünlerinizde site komisyonu yoktur: kurye, ürünün bedelini teslimatta müşteriden tahsil edip doğrudan size verir; cüzdanınıza eklenmez." },
        { title: "Toptan mallara göz atın", text: "Toptan pazarlarını açıp toptancıların ürünlerine göz atın. Toptan fiyatlar yalnızca mağaza sahiplerine ve tüccarlara gösterilir, müşterilere asla.", note: "Yalnızca toptancının sanal mağazalara açtığı ürünleri yeniden satabilirsiniz." },
        { title: "Bulut stokunuza ekleyin", text: "Uygun bir üründe \"Mağazama ekle\"ye dokunun; ürün, satın almadan veya ödeme yapmadan bulut stokunuza eklenir. Toptancı ürünü depolar, hazırlar ve gönderir.", note: "Her mağazanın ekleyebileceği toptan ürün sayısında bir sınır vardır." },
        { title: "Satış fiyatını belirleyin", text: "Müşteri fiyatını seçin. Sistem toptan fiyatın üzerinde bir alt sınır koyar ve kaydetmeden önce marjınızı, sitenin payını ve net kârınızı gösterir.", note: "Örnek: toptan 20.000, satış 30.000 → marj 10.000; site bunun %{rate}'sini ({fee}) alır, size {net} kalır." },
        { title: "Tanıtın ve siparişleri takip edin", text: "Mağaza bağlantınızı paylaşın ve ürünlerini tanıtın. Bir müşteri toptan ürün sipariş ettiğinde, toptancının hazırlayıp göndermesi için siparişi kabul edin.", note: "24 saat içinde kabul etmediğiniz sipariş otomatik iptal edilir ve ihlal sayılır; 3 ihlalden sonra mağaza dondurulur ve yönetim yeniden etkinleştirene kadar giriş yapamazsınız. Kâr garanti değildir; satışlar seçimlerinize, tanıtımınıza ve takibinize bağlıdır." },
        { title: "Kazancınızı alın", text: "Toptan mallarda: sipariş teslim edilip teslim koduyla onaylandığında kârınız hesaplanır ve sitenin ödeme sistemi üzerinden cüzdanınıza eklenir; ödeme talep edebilirsiniz. Kendi ürünlerinizin bedelini ise kurye teslimatta doğrudan size verir.", note: "Sitenin payı yalnızca toptan ve satış fiyatı arasındaki farktan alınır, fiyatın tamamından asla." },
      ],
      calloutTitle: "Şeffaflık fırsatın bir parçası",
      calloutText: "Mağaza açmak veya ürün eklemek ödeme gerektirmez. Yeniden satılan toptan mallarda sitenin payı, satış tamamlandığında marjdan alınır ve ödemeler site cüzdanları üzerinden yapılır. Kendi ürünlerinizde site komisyonu yoktur; kurye bedelini doğrudan size verir. Başarılı bir mağaza iyi ürün seçimi, tanıtım ve sipariş takibi ister; kârlar gerçek satışlara bağlıdır.",
    },
    trade: {
      tab: "Toptancıyım",
      tabHint: "Satış noktalarınızı genişletin",
      title: "Katılımcı mağazaları mallarınız için ek satış noktalarına dönüştürün",
      intro: "Toptan bir dükkânınız veya deponuz mu var? Mallarınızı Irak Pazarı'nda gerçek ve sanal mağaza sahiplerine sunun. Her mağaza ürünlerinizi seçip müşterilerine perakende fiyatla satar; dükkânınızın ve mevcut kanallarınızın yanında satış noktalarınız genişler, belirlediğiniz toptan fiyat sizde kalır.",
      badges: ["Ek satış noktaları", "Toptan fiyatı siz belirlersiniz", "Dükkânınız ve kanallarınız aynı kalır"],
      flowTitle: "Mallarınız müşterilere nasıl ulaşır?",
      flow: ["Siz: toptancı", "Gerçek ve sanal mağazalar", "Perakende fiyatla müşteriler"],
      highlights: [
        { title: "Sizinle büyüyen satış noktaları", text: "Ürünlerinizi ekleyen her aktif mağaza, şube açmadan veya dükkân kiralamadan mallarınız için yeni bir satış noktası olabilir." },
        { title: "Toptan fiyatınız korunur", text: "Toptan teklifler ve fiyatlar müşterilere gösterilmez; böylece toptan kanalınız tüketicinin önünde perakende fiyatlarla rekabet etmez." },
      ],
      steps: [
        { title: "Toptancı olarak kaydolun", text: "\"Toptancı olarak kaydol\"u seçip işletme bilgilerinizi, başka bir hesapta kullanılmayan bir telefon numarası ve e-postayla girin. Yönetim, hesabı etkinleştirmeden önce inceler.", note: "Toptancı hesabı malları mağazalara sunar; tüketiciye toptan fiyatla satış yapmaz." },
        { title: "Mallarınızı ve toptan fiyatları ekleyin", text: "Gerçek fotoğraflar, net bir açıklama, toptan fiyat, mevcut miktar ve minimum sipariş ekleyin.", note: "Toptan mallar şu kategorilerde listelenir: Alışveriş, Spor, Çocuk, Tıbbi malzemeler ve Yedek parça." },
        { title: "Mağazaların yeniden satmasına izin verin", text: "Mağazaların önceden satın almadan yeniden satmasını istediğiniz ürünlerde \"Sanal mağazalara açık\" seçeneğini açın.", note: "Bunu ürün ürün seçersiniz." },
        { title: "Teklifleriniz mağazalara ulaşır", text: "Toptan teklifler ve fiyatlar yalnızca kayıtlı mağaza sahiplerine ve tüccarlara gösterilir; müşterilere toptan fiyat olarak gösterilmez.", note: "Bu ayrım, toptan fiyatların müşterinin önünde perakendeyle rekabet etmesini önler." },
        { title: "Mağazalar mallarınızı perakende satar", text: "Mağaza sahipleri uygun ürünlerinizi toptanın üzerinde bir perakende fiyatla mağazalarına ekler ve sistem bir alt sınır uygular. Dükkânlar mallarınızı minimum siparişe uyarak doğrudan toptan da alabilir.", note: "Her aktif mağaza yeni bir satış noktası olabilir, ancak mağaza ve satış sayısı garanti değildir." },
        { title: "Siparişleri hazırlayın ve ödemenizi alın", text: "Bir müşteri ürününüzü bir mağazadan sipariş edip mağaza kabul ettiğinde ürünü hazırlar ve gönderirsiniz; teslimat onaylanınca size düşen toptan tutar cüzdanınıza eklenir.", note: "Dükkânınızı veya mevcut satış kanallarınızı bırakmanız gerekmez." },
        { title: "Doğrudan tüketiciye mi satmak istiyorsunuz?", text: "Toptancı hesabı tüketiciye satış yapamaz ve mağazaya dönüştürülemez. Perakende satmak için farklı bir telefon numarası ve e-postayla ayrı bir müşteri hesabı açın, buradan sanal mağaza etkinleştirin ve ürünlerinizi perakende fiyatla listeleyin.", note: "Sitenin politikası perakende fiyatın toptan fiyattan yüksek olmasıdır ve toptan fiyatlar müşterilere asla gösterilmez." },
      ],
      calloutTitle: "Toptan dükkânlara, perakende mağazalar aracılığıyla",
      calloutText: "Toptan teklifler ve fiyatlar tüccarlara ve mağaza sahiplerine ayrılmıştır; müşterilere toptan teklif olarak gösterilmez. Müşteriler ürünlerinizi, mağazalar onları perakende fiyatla listelediğinde görür.",
    },
  },
};

const zh: GuideCopy = {
  eyebrow: "✦ 伊拉克市场使用指南",
  title: "您想如何使用伊拉克市场？",
  subtitle: "无论您是买家、卖家、店主还是批发商，都能找到适合您的方式。",
  close: "关闭",
  rolesLabel: "选择您使用伊拉克市场的方式",
  stepsLabel: "指南步骤",
  stepCounter: "第 {n} 步，共 {total} 步",
  prev: "上一步",
  next: "下一步",
  done: "完成 ✓",
  currency: "伊拉克第纳尔",
  calc: {
    title: "估算您转售批发商品的利润",
    sub: "输入批发价和您的售价，查看差价、网站份额和您的净利润。仅为示例，并非销售预测。",
    cost: "批发价（伊拉克第纳尔）",
    price: "售价（伊拉克第纳尔）",
    margin: "差价",
    fee: "网站份额（{rate}%）",
    net: "您的净利润",
    disclaimer: "网站份额（{rate}%）仅从转售差价中收取，在订单交付后按网站设置中的比例计算，您的净利润计入钱包。您商店的自有产品不收取任何网站佣金，货款由配送员直接交给您。",
    belowCost: "要获得利润，售价必须高于批发价，且不低于系统设定的最低价。",
    belowMin: "该价格低于系统对此商品接受的最低价：{min}。请选择不低于该价格的售价。",
  },
  friday: {
    summary: "周五市场呢？",
    text: "您可以发布周五市场专属广告，它只在周五（伊拉克时间）出现在该市场。如在其他日期发布，会自动安排在下一个周五显示。它与批发报价和商店是不同的渠道。",
    cta: "在周五市场发布",
  },
  cta: {
    buy: "开始搜索",
    sell: "发布广告",
    storeOpen: "免费开店",
    storeManage: "前往我的商品",
    storeSeparate: "用单独账户开店",
    storeMore: "进一步了解虚拟商店",
    tradeRegister: "注册为批发商",
    tradeDashboard: "前往批发面板",
    sellStoreOwn: "为商店添加自有产品",
    sellWholesale: "添加批发报价",
    separateCustomer: "如需发布个人广告，请开设单独的顾客账户",
  },
  roles: {
    buy: {
      tab: "我想购买",
      tabHint: "浏览商品并下单",
      title: "按分类和省份查找商品",
      intro: "搜索您需要的广告或商品，阅读详情，然后按该报价支持的方式完成交易。",
      badges: ["清晰的分类", "按省份搜索", "联系卖家或向商店下单"],
      steps: [
        { title: "搜索", text: "浏览分类或输入商品名称，并选择省份以获得离您更近的结果。", note: "无需注册即可浏览。" },
        { title: "查看详情", text: "打开商品，查看照片、描述、价格、位置和购买方式。", note: "批发报价不向顾客展示；这些商品通过商店以零售价提供给您。" },
        { title: "完成下一步", text: "在购物、运动、儿童、医疗用品和汽车配件分类中，无论卖家是商店还是个人，都通过购物车购买并货到付款。在汽车、房产、服务等其他分类中，则直接联系广告发布者。", note: "收货时检查商品，满意后再提供收货码。" },
      ],
      calloutTitle: "下单前了解购买方式",
      calloutText: "购买方式由分类决定：商品类分类使用购物车、填写收货信息并货到付款；其他分类通过买卖双方直接联系并协商完成。",
    },
    sell: {
      tab: "我想出售物品",
      tabHint: "发布您的广告",
      title: "把闲置物品变成交易机会",
      intro: "为新的或二手物品发布广告，并在伊拉克市场内与感兴趣的买家沟通。",
      badges: ["清晰的照片", "价格和位置", "与买家沟通"],
      steps: [
        { title: "登录", text: "创建账户或登录，然后选择“发布广告”。", note: "发布普通个人广告无需联系管理员。" },
        { title: "准备广告", text: "添加照片、标题、描述、价格、省份和正确的分类。", note: "真实的照片和准确的信息让您的广告更可信。" },
        { title: "跟进买家", text: "发布广告并跟进：在购物、运动、儿童、医疗用品和汽车配件分类中，订单通过购物车到达，您可在账户菜单中跟进；在其他分类中，买家会直接联系您。", note: "在直接联系的分类中，交易由您与买家协商完成。" },
      ],
      calloutTitle: "简单的自助发布",
      calloutText: "每个账户都有一定数量的免费广告；如需更多，请联系管理员。商店和批发商账户通过其商店和报价销售，而非个人广告。",
    },
    store: {
      tab: "我要开虚拟商店",
      tabHint: "无需购买即可销售批发商品",
      title: "在家开展销售业务，无需购买库存",
      intro: "以您的名义免费开设虚拟商店，销售您的自有产品（如有），以及无需预先购买的合格批发商品。您挑选商品并在规则范围内设定售价，然后推广商店、跟进订单。您自有产品的货款由配送员向顾客收取后直接交给您；批发商品的利润在交易完成后计入您的钱包。",
      badges: ["免费开店", "自有产品：无佣金", "批发商品：无需预购"],
      highlights: [
        { title: "销售自有产品，网站不收佣金", text: "当您销售自己拥有的产品时，配送员向顾客收取货款并直接交给您。网站不从中抽取任何份额，款项也不经过您在网站上的钱包。" },
        { title: "无库存转售批发商品", text: "无需预先购买即可上架合格的批发商品；网站份额仅为您差价的 {rate}%，而非售价，您的利润通过网站钱包发放。" },
      ],
      steps: [
        { title: "免费开店", text: "创建普通账户，然后在“与我们合作”页面激活虚拟商店，并为其取一个顾客可见的名称。开店和添加产品均免费，无需实体店。", note: "激活商店会把您的账户变为商店账户：不能再发布个人广告，也不能向商店或个人购买。如需这些功能，请使用单独的顾客账户。" },
        { title: "添加自有产品", text: "如果您有自己的货物，可在“我的商品”中添加并设定价格。有订单时由您备货并交给配送员。", note: "网站不对您的自有产品收取佣金：配送员在交付时向顾客收取货款并直接交给您，不计入您的钱包。" },
        { title: "浏览批发商品", text: "进入批发市场浏览批发商的商品。批发价只向店主和商家展示，从不向顾客展示。", note: "您只能转售批发商开放给虚拟商店的商品。" },
        { title: "加入您的云库存", text: "在合格商品上点击“添加到我的商店”，即可加入您的云库存，无需购买或付款。批发商负责存储、备货和发货。", note: "每个商店可添加的批发商品数量有上限。" },
        { title: "设定售价", text: "为顾客选择售价。系统会设定高于批发价的最低价，并在保存前显示您的差价、网站份额和净利润。", note: "示例：批发价 20,000，售价 30,000 → 差价 10,000；网站收取其中 {rate}%（{fee}），您保留 {net}。" },
        { title: "推广并跟进订单", text: "分享您的商店链接并推广商品。顾客订购批发商品时，请接受订单，以便批发商备货发货。", note: "24 小时内未接受的订单会被自动取消并记一次违规；累计 3 次违规后商店将被冻结，在管理员重新激活前无法登录。利润并无保证；销售取决于您的选品、推广和跟进。" },
        { title: "获得收益", text: "批发商品：订单交付并以收货码确认后，您的利润将被计算并通过网站的结算系统计入钱包，您可以申请提现。您的自有产品则由配送员在交付时直接把货款交给您。", note: "网站份额只按批发价与售价之间的差额计算，绝不按整个价格计算。" },
      ],
      calloutTitle: "透明也是机会的一部分",
      calloutText: "开店和添加商品无需任何付款。转售批发商品时，网站份额在交易完成时从差价中收取，款项通过网站钱包结算。您的自有产品不收网站佣金，货款由配送员直接交给您。成功的商店需要合适的选品、推广和订单跟进，利润取决于实际销售。",
    },
    trade: {
      tab: "我是批发商",
      tabHint: "拓展销售渠道",
      title: "让参与的商店成为您货物的额外销售渠道",
      intro: "您有批发店或仓库吗？在伊拉克市场向实体店和虚拟商店的店主展示您的货物。每家商店挑选您的商品并以零售价卖给顾客，在您的店铺和现有渠道之外拓展销售渠道，同时您保留自己设定的批发价。",
      badges: ["额外的销售渠道", "批发价由您设定", "保留您的店铺和渠道"],
      flowTitle: "您的货物如何到达顾客？",
      flow: ["您：批发商", "实体店和虚拟商店", "以零售价购买的顾客"],
      highlights: [
        { title: "随您成长的销售渠道", text: "每一家添加您商品的活跃商店，都可能成为您货物的新渠道，无需开分店或租店铺。" },
        { title: "批发价受到保护", text: "批发报价和价格不向顾客展示，因此您的批发渠道不会在消费者面前与零售价竞争。" },
      ],
      steps: [
        { title: "注册为批发商", text: "选择“注册为批发商”，并用未被其他账户使用的电话号码和邮箱填写经营信息。管理员会在激活前审核账户。", note: "批发商账户向商店供货，不以批发价直接卖给消费者。" },
        { title: "添加货物和批发价", text: "添加真实照片、清晰描述、批发价、可用数量和最低订购量。", note: "批发商品可发布在以下分类：购物、运动、儿童、医疗用品和汽车配件。" },
        { title: "允许商店转售", text: "对希望商店无需预购即可转售的商品，开启“向虚拟商店开放”选项。", note: "您可逐个商品进行选择。" },
        { title: "您的报价送达商店", text: "批发报价和价格只向已注册的店主和商家展示，不以批发价向顾客展示。", note: "这种区分避免批发价在顾客面前与零售价竞争。" },
        { title: "商店以零售方式销售您的货物", text: "店主以高于批发价的零售价将您的可用商品加入店铺，系统会强制最低价。店铺也可以按最低订购量直接向您批发采购。", note: "每家活跃商店都可能成为新渠道，但商店数量和销量并无保证。" },
        { title: "处理订单并获得货款", text: "顾客在某家商店订购您的商品且商店接受后，您负责备货和发货；交付确认后，应付给您的批发金额会计入您的钱包。", note: "您无需放弃自己的店铺或现有销售渠道。" },
        { title: "想直接卖给消费者？", text: "批发商账户不能向消费者销售，也不能转为商店。如需零售，请用不同的电话号码和邮箱创建单独的顾客账户，从该账户激活虚拟商店，并以零售价上架您的商品。", note: "网站政策要求零售价高于批发价，且批发价从不向顾客展示。" },
      ],
      calloutTitle: "批发面向店铺，零售通过商店",
      calloutText: "批发报价和价格仅供商家和店主查看，不作为批发报价向顾客展示。顾客会在商店以零售价上架您的商品时看到它们。",
    },
  },
};

export const GUIDE_COPY: Record<string, GuideCopy> = { ar, en, tr, zh };
